// Daily Brief + explanation layer.
//
// Per the product architecture, the AI/explanation layer NEVER determines the
// score. It reads the deterministic engine output and produces plain-language
// text separated into FACT / INTERPRETATION / UNCERTAINTY. It must never make
// price predictions.

import { INDICATORS_BY_ID } from './indicators';
import { SIGNAL_META, PHASE_META } from './scoring';
import { isPointMove } from './changeDetection';
import type { ScoreSnapshot, ChangeEvent } from './types';

export interface Brief {
  liquidityScore: number;
  liquidityEmoji: string;
  bchScore: number;
  bchEmoji: string;
  cycle: string;
  whatChanged: string[];
  biggestRisk: string;
  strengthen: string;
  watchNext: string[];
  wordCount: number;
}

const UNCERTAINTY =
  'These conditions have historically been associated with stronger risk-asset performance, but they do not predict BCH\u2019s future price.';

export function generateBrief(
  snapshot: ScoreSnapshot,
  changes: ChangeEvent[],
  trend30d: { liquidity: number; bch: number },
): Brief {
  const liqEmoji = SIGNAL_META[snapshot.liquidity.state].emoji;
  const bchEmoji = SIGNAL_META[snapshot.bch.state].emoji;

  const whatChanged = changes.slice(0, 3).map((c) => {
    const dir = c.pct >= 0 ? 'increased' : 'decreased';
    const def = INDICATORS_BY_ID[c.indicatorId];
    // Point-mode indicators (percentage-unit) report a point move, not a
    // "% of a %" — keep this consistent with the change cards.
    const unit = isPointMove(c.indicatorId)
      ? `${Math.abs(c.pct).toFixed(1)} pts`
      : `${Math.abs(c.pct).toFixed(1)}%`;
    return `${c.title} — ${def.short} ${dir} ${unit} (${c.window}).`;
  });
  if (whatChanged.length === 0) {
    whatChanged.push('No meaningful changes detected in the current window.');
  }

  // Biggest risk = the most negative-contributing group in liquidity.
  const negativeGroup = [...snapshot.liquidity.groupContributions]
    .sort((a, b) => a.points - b.points)[0];
  const biggestRisk = negativeGroup && negativeGroup.points < 0
    ? `${negativeGroup.label} conditions are the largest drag on liquidity (${negativeGroup.points} pts).`
    : 'Long-end Treasury yields remain the primary structural risk.';

  const strengthen =
    'Confirmation would require improving Fed/global liquidity, a constructive BTC trend, and a sustained BCH/BTC breakout occurring together.';

  const watchNext = Array.from(
    new Set(changes.slice(0, 3).flatMap((c) => c.watchNext)),
  ).slice(0, 5);
  if (watchNext.length === 0) {
    watchNext.push('USD/JPY', 'FIMA', '10Y Treasury', 'BTC', 'BCH/BTC');
  }

  const brief: Brief = {
    liquidityScore: snapshot.liquidity.score,
    liquidityEmoji: liqEmoji,
    bchScore: snapshot.bch.score,
    bchEmoji: bchEmoji,
    cycle: PHASE_META[snapshot.phase].label,
    whatChanged,
    biggestRisk,
    strengthen,
    watchNext,
    wordCount: 0,
  };

  const text = [
    ...whatChanged,
    biggestRisk,
    strengthen,
    ...watchNext,
    UNCERTAINTY,
  ].join(' ');
  brief.wordCount = text.split(/\s+/).length;

  return brief;
}

export const UNCERTAINTY_DISCLAIMER = UNCERTAINTY;
